import type { LoaderFunctionArgs } from "react-router";

import { topasAggregatesClient } from "~/features/integration/topas-aggregates/client.server";
import { fetchJson } from "~/utils/fetch-json";
import { fetchFromKotlinBFFIfAllowed } from "~/utils/kotlin-bff.server";

import { useQuery } from "@tanstack/react-query";
import { OperationalInformationEventAggregateFromJSON } from "@vy-clients/topas-aggregates";
import { toMilliseconds } from "duration-fns";
import type { Jsonify } from "type-fest";

export type EventsData = Jsonify<
  Awaited<ReturnType<ReturnType<typeof fetchFromTopasAggregates>>>
>;

const fetchFromTopasAggregates =
  (context: LoaderFunctionArgs["context"]) => async () => {
    const client = topasAggregatesClient(context);
    const user = context.session?.user;

    const shouldIncludeSensitiveEvents =
      user?.rbac.isConductor || user?.rbac.isDriver || user?.rbac.isDeveloper;

    return await client.getAllOperationalInformationEventAggregates({
      includeSensitiveEvents: shouldIncludeSensitiveEvents,
    });
  };

export const loader = async ({ context }: LoaderFunctionArgs) => {
  return fetchFromKotlinBFFIfAllowed({
    context,
    // Kotlin-BFF tar ingen query-params her. EventsController utleder
    // includeSensitiveEvents selv fra sesjonen med samme logikk som fallbacken
    // (isDriver || isConductor || isDeveloper).
    path: "/v0/train-info/events",
    fallbackFetcher: fetchFromTopasAggregates(context),
    kotlinBffDataValidator: OperationalInformationEventAggregateFromJSON,
  });
};

export const EVENTS_QUERY_KEY = ["events"] as const;

export const eventsQueryFn = () => fetchJson<EventsData>("/api/events");

export const useEvents = () => {
  return useQuery({
    queryKey: EVENTS_QUERY_KEY,
    queryFn: eventsQueryFn,
    staleTime: toMilliseconds({ seconds: 10 }),
    refetchInterval: toMilliseconds({ seconds: 30 }),
    refetchIntervalInBackground: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};
